import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PaginatorControl from './components/paginatorControl';
import logger from 'src/analytics/logger';

const Paginator = ({
  parentId /* Id of the container that holds the elements to pagenate */,
  elementClass /* CSS class of the elements in the container to paginate */,
  elementsChangedEvent /* Name of a custom event on the container you can fire to cause the paginator */,
  /* to re-initialize.  The paginator will only operate on elements that are visible */
  elementsPerPage /* Maximum number of elements to show per page */,
  maxPagesToShow = 5 /* Maximum number of page buttons to show in the control at one time */
}) => {
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [mediaChangeCount, setMediaChangeCount] = useState(0);

  const getAllElements = () => {
    let parent = document.getElementById(parentId);
    return Array.from(parent.querySelectorAll(`.${elementClass}`));
  };

  const showElementsOnPage = (page) => {
    getAllElements().forEach((element) => {
      element.style.display =
        element.dataset.onpage === page.toString() ? 'block' : 'none';
    });
  };

  const getVisibleElements = () =>
    getAllElements().filter((e) => e.style.display !== 'none');

  const clearPageForElements = () => {
    getAllElements().forEach((element) => {
      if (element.dataset) {
        Reflect.deleteProperty(element.dataset, 'onpage');
      }
    });
  };

  const getElementsByPage = () => {
    let elementsByPage = [];
    let elements = getVisibleElements();
    for (let index = 0; index < elements.length; index += elementsPerPage) {
      elementsByPage.push(elements.slice(index, index + elementsPerPage));
    }
    return elementsByPage;
  };

  const setPageForElements = () => {
    clearPageForElements();
    let pageNumber = 1;
    getElementsByPage().forEach((page) => {
      page.forEach((element) => (element.dataset.onpage = pageNumber));
      pageNumber++;
    });
  };

  const handleMediaChanged = () => {
    let elementCount = getVisibleElements().length;
    setPageCount(Math.ceil(elementCount / elementsPerPage));
    setPageForElements();
    setMediaChangeCount((current) => current + 1);
    setCurrentPage(1);
  };

  useEffect(() => {
    let parent = document.getElementById(parentId);
    parent.addEventListener(elementsChangedEvent, handleMediaChanged);
    handleMediaChanged();
    return () =>
      parent.removeEventListener(elementsChangedEvent, handleMediaChanged);
  }, []); // Only run on initial render

  useEffect(() => {
    showElementsOnPage(currentPage);
  }, [currentPage, mediaChangeCount]);

  const logPageChange = (page) => {
    logger.trackElementClicked({
      elementId: 'Pagination Page',
      label: page,
      page: 'Explore',
      path: '/learn/explore'
    });
  };

  const scrollToTop = () => {
    let parent = document.getElementById(parentId);
    parent.scrollIntoView();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    logPageChange(page);
    scrollToTop();
  };

  if (pageCount > 1) {
    return (
      <PaginatorControl
        pageCount={pageCount}
        currentPage={currentPage}
        maxPagesToShow={maxPagesToShow}
        onPageSelected={handlePageChange}
      />
    );
  }
  return null;
};

Paginator.propTypes = {
  parentId: PropTypes.string,
  elementClass: PropTypes.string,
  elementsChangedEvent: PropTypes.string,
  elementsPerPage: PropTypes.number,
  maxPagesToShow: PropTypes.number
};

export default Paginator;
